import { Formik, Form, Field } from "formik";
import { useState } from "react";
import SectionDescription from "components/lib/Shared/SectionDescription";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Input from "components/lib/Shared/Input";
import { handleFilesUpload } from "utils/filesUtils";
import { updateTransaction } from "state/slices/transaction";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import ESignatureManualDropDown from "components/lib/Shared/ESignatureManualDropDown";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { addComment, editComment } from "state/slices/comment";
import _get from "lodash/get";
import { Menu, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect } from "react";
import { usePopper } from "hooks/usePopper";
import { ChevronDownIcon } from "@heroicons/react/outline";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { useToast } from "hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import {
  brsUploadsValidationSchema,
  getFileUploadsValidationSchemaAsPerTypeOfChange,
} from "./validationSchemas";
import {
  getDocumentsAsPerTypeOfChange,
  brsFilingReceipt,
  courtOrderDocuments,
} from "./transactionDocumentData";
import { Constants } from "config/constants";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import ManualTransactionReviewContext from "context/ManualTransactionReviewContext";
import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import { ChevronRightIcon } from "@heroicons/react/solid";
import Checkbox from "components/lib/Shared/Checkbox";

const TransactionUpload = ({
  transaction,
  goToNextStep,
  esignatureIsSelected,
  setESignatureIsSelected,
  goToPrevStep,
  handleConfirmDetails,
  isRegistrar = false,
  isLastStep,
  handleBackBtn,
  isViewMode = false,
  isInUpdateCompanyDetailsContext = false,
  setESignatureIsForAdmin,
}) => {
  /** Redux hooks to manage state and dispatch actions */
  const dispatch = useDispatch();
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const isManualTransaction =
    transaction?.transactionType === Constants.TRANSACTION_TYPES.MANUAL;
  const storageKey = `BRS-${transaction._id}`;
  let localStorageBrsValues = {};
  if (JSON.parse(localStorage.getItem(storageKey))) {
    localStorageBrsValues = JSON.parse(localStorage.getItem(storageKey));
    Object.entries(localStorageBrsValues).forEach(([key, value]) => {
      const { name, type, lastModified, dataUrl } = localStorageBrsValues[key];
      const base64 = dataUrl.split(",")[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      localStorageBrsValues[key] = new File([bytes], name, {
        type,
        lastModified,
      });
    });
  }

  /** toast hook for alert messages */
  const { toast } = useToast();

  /** react-router-dom hooks */
  const history = useHistory();
  /** Check if transaction has reached the super admin */
  const isInBRS =
    transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW ||
    transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II;

  let changedOfficial = {};
  if (transaction?.individualOfficials?.length > 0) {
    changedOfficial = transaction?.individualOfficials?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  if (transaction?.corporateOfficials?.length > 0) {
    changedOfficial = transaction?.corporateOfficials?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  if (transaction?.beneficialOwners?.length > 0) {
    changedOfficial = transaction?.beneficialOwners?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  let changedFields = {};
  if (changedOfficial?.changedFields) {
    changedFields = changedOfficial?.changedFields;
  }

  const documents = getDocumentsAsPerTypeOfChange(transaction, changedFields); // eg. APPOINTMENT of Authorized Person
  const brsReciptDocs = brsFilingReceipt();
  const courtOrderDocs = courtOrderDocuments();

  let fileNames = {};
  documents.forEach((doc) => {
    fileNames[doc.name] = "";
  });

  let fileUploads = {};
  let preSignedBusinessDocuments = {};

  if (
    transaction?.fileUploads &&
    Object.keys(transaction?.fileUploads).length > 0
  ) {
    fileUploads = transaction.fileUploads;
  } else {
    fileUploads = {
      ...fileNames,
    };
  }

  if (
    transaction?.preSignedBusinessDocuments &&
    Object.keys(transaction?.preSignedBusinessDocuments).length > 0
  ) {
    preSignedBusinessDocuments = transaction.preSignedBusinessDocuments;
  } else {
    preSignedBusinessDocuments = {
      ...fileNames,
    };
  }

  const [initialValues, setInitialValues] = useState({
    brsReferenceNumber: transaction?.brsReferenceNumber || "",
    preSignedBusinessDocuments,
    fileUploads: isInBRS
      ? {
          ...fileUploads,
          ...localStorageBrsValues,
          ["brsFilingReceipt"]: "",
        }
      : {
          ...fileUploads,
          ...localStorageBrsValues,
        },
    upholdConversionResolution:
      transaction?.companyChangedFields?.upholdConversionResolution || false,
    cancelConversionResolution:
      transaction?.companyChangedFields?.cancelConversionResolution || false,
    registrarApproved:
      transaction?.companyChangedFields?.registrarApproved || false,
    registrarRejected:
      transaction?.companyChangedFields?.registrarRejected || false,
  });

  /** Initial Formik values */

  /** Use for the dropdown button */
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  /** Validation schema for Formik */
  let validationSchema = {};

  if (isRegistrar || isInUpdateCompanyDetailsContext) {
    /** Schema to contain only fileUploads and registrationNumber for validation*/
    validationSchema = getFileUploadsValidationSchemaAsPerTypeOfChange(
      "fileUploads",
      transaction,
      changedFields
    );
  } else if (isInBRS) {
    /** simailar to fileUploadsValidationSchema with added attribute for filing receipts */
    validationSchema = brsUploadsValidationSchema("fileUploads", transaction);
  } else {
    /** Schema to contain only preSignedBusinessDocuments and registrationNumber for validation*/
    validationSchema = getFileUploadsValidationSchemaAsPerTypeOfChange(
      "preSignedBusinessDocuments",
      transaction,
      changedFields
    );
  }

  let officialIndex = 0;
  if (!isInUpdateCompanyDetailsContext) {
    officialIndex = transactionUnderReview.items.transactions.findIndex(
      (trx) => trx._id === transaction._id
    );
  }

  /** Company data */
  const { company } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const { shares } = useSelector((state) => state).sharesTableSlice;
  const registeredOfficeSlice = useSelector(
    (state) => state.registeredOfficeSlice
  );
  const originalCompanyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );
  const beneficialOwners = useSelector(
    (state) => state.beneficialOwnersTableSlice.beneficialOwners
  );
  const transactionComments = comments.filter(
    (comment) => comment.transaction === transaction._id
  );
  const brsFormUploadComments = transactionComments.filter(
    (comment) => comment.formName === "brsFormUpload"
  );

  /** handle form events */

  const handleGoBack = () => {
    if (!isRegistrar) {
      goToPrevStep();
    } else {
      history.push("/");
    }
  };

  /** Handle form submission */
  const handleSubmit = async (formData) => {
    let transactionData = {};
    if (!isRegistrar) {
      if (isInBRS) {
        /** If the transaction is in BRS, we only update status */
        const uploadedFiles = await handleFilesUpload(formData.fileUploads);
        transactionData = {
          fileUploads: {
            ...uploadedFiles,
          },
          status: formData?.status,
          brsReferenceNumber: formData?.brsReferenceNumber,
          transactionOfficials: transactionUnderReview?.items?.officials,
        };
      } else if (isInUpdateCompanyDetailsContext) {
        const uploadedFiles = await handleFilesUpload(formData.fileUploads);
        transactionData = {
          fileUploads: {
            ...uploadedFiles,
          },
          brsReferenceNumber: formData?.brsReferenceNumber,
        };
        if (
          transaction?.status ===
          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.AWAITING_COURT_ORDER
        ) {
          const companyChangedFields = {
            ...transaction.companyChangedFields,
            upholdConversionResolution: formData?.upholdConversionResolution,
            cancelConversionResolution: formData?.cancelConversionResolution,
          };
          transactionData = {
            ...transactionData,
            companyChangedFields,
          };
        }
        localStorage.removeItem(storageKey);
      } else {
        /** Update only preSignedBusinessDocuments if is Org Admin */
        const uploadedFiles = await handleFilesUpload(
          formData.preSignedBusinessDocuments
        );
        transactionData = {
          preSignedBusinessDocuments: {
            ...uploadedFiles,
          },
          brsReferenceNumber: formData?.brsReferenceNumber,
        };
      }
    } else {
      /** Update only fileUploads if is Registrar */
      const uploadedFiles = await handleFilesUpload(formData.fileUploads);
      transactionData = {
        fileUploads: {
          ...uploadedFiles,
        },
        brsReferenceNumber: formData?.brsReferenceNumber,
        status: Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW,
      };
    }
    const updatedTransaction = await dispatch(
      updateTransaction({
        trxId: transaction?._id,
        data: {
          ...transactionData,
        },
        queryParams: {
          updateRelatedTransactions: true, // update batched transactions
        },
      })
    );

    if (!isLastStep || (isLastStep && isInUpdateCompanyDetailsContext)) {
      goToNextStep();
    }

    if (formData.eSignatureIsSelected) {
      setESignatureIsSelected(true);
      setESignatureIsForAdmin(true);
    }
    if (formData.manualSigningIsSelected) {
      handleConfirmDetails();
    }

    if (isRegistrar && !isInUpdateCompanyDetailsContext) {
      history.push("/");
    }
    if (isInBRS) {
      handleBackBtn();
    }

    toast("success", "Updated transaction successfully.");
  };

  /** Adding and saving comments left on files uploaded */
  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: transaction._id,
          },
        })
      );
    }
  };

  //Review documents for registrar decision on company restoration transaction
  const registrarDecisionDocuments = () => {
    if (isManualTransaction) {
      if (
        transaction?.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
      ) {
        if (transaction?.fileUploads?.gazetteNotice) {
          return [
            {
              label: "Gazette Notice",
              name: "gazetteNotice",
              accept: "application/pdf",
            },
          ];
        } else if (transaction?.fileUploads?.letterOfRejection) {
          return [
            {
              label: "Letter of Rejection",
              name: "letterOfRejection",
              accept: "application/pdf",
            },
          ];
        } else {
          return [];
        }
      } else {
        return [];
      }
    } else {
      return [];
    }
  };

  return (
    <div>
      <div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({
            isSubmitting,
            values,
            errors,
            setFieldValue,
            submitForm,
            touched,
          }) => {
            return (
              <Form className="mt-4">
                <PersistFileUploadsToLocalStorage
                  values={values}
                  storageKey={storageKey}
                />
                <div className="flex gap-4">
                  {!isInUpdateCompanyDetailsContext &&
                    !isManualTransaction &&
                    transaction.transactionType !==
                      Constants.TRANSACTION_TYPES.MANUAL && (
                      <div className="w-full">
                        <SectionDescription
                          title="Unsigned BRS forms"
                          description="Please upload the incorporation forms generated from the BRS Portal, to be signed by the promoters and subscribers of the Company."
                        />
                        <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                          <div className="px-4 py-6 space-y-6 sm:p-6">
                            <div className="flex items-center justify-between">
                              <label
                                htmlFor="unsignedReferenceNumber"
                                className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal after:content-['*'] after:ml-0.5 after:text-red-500"
                              >
                                BRS Reference Number
                              </label>
                              <div className="flex flex-col w-full">
                                <Field
                                  name="brsReferenceNumber"
                                  id="unsignedReferenceNumber"
                                  placeholder="Add BRS Reference Number"
                                  component={Input}
                                  className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                />
                              </div>
                            </div>
                            <div className="mb-4">
                              <h3 className="text-lg font-medium leading-6 text-gray-900">
                                Upload documents
                              </h3>
                              <p className="mt-1 text-sm text-gray-500">
                                Please provide the correct documents as
                                instructed.
                              </p>
                            </div>
                            <Field
                              name="preSignedBusinessDocuments"
                              component={FileUploadForm}
                              documentData={documents.map(
                                (document) => document
                              )}
                              onlyPreview={isInBRS}
                              showDocumentLabel={isInBRS}
                              addReviewComment={isInBRS}
                              hideResolveAllCheckbox={!isInBRS}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  {(isInBRS ||
                    isRegistrar ||
                    isInUpdateCompanyDetailsContext ||
                    isManualTransaction ||
                    transaction.transactionType ===
                      Constants.TRANSACTION_TYPES.MANUAL) && (
                    <div className="w-full">
                      <SectionDescription
                        title="Signed BRS forms"
                        description="Please upload the incorporation forms generated from the BRS Portal, to be signed by the promoters and subscribers of the Company."
                      />
                      <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                        {brsFormUploadComments.length > 0 && (
                          <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                            <CommentsList
                              comments={brsFormUploadComments}
                              toggleCommentStatus={true}
                            />
                            <p className="mt-6 text-sm text-primary">
                              Comments: {brsFormUploadComments.length} of{" "}
                              {brsFormUploadComments.length}
                            </p>
                          </div>
                        )}
                        <div className="px-4 py-6 space-y-6 sm:p-6">
                          {isInBRS || isManualTransaction || isViewMode ? (
                            <FormFieldsCard
                              fields={[
                                {
                                  key: "BRS Reference Number",
                                  name: "brsReferenceNumber",
                                  value: transaction?.brsReferenceNumber,
                                },
                              ]}
                              addReviewComment={isInBRS || isManualTransaction}
                              resolvedFields={_get(
                                transaction,
                                "resolvedFields"
                              )}
                              hideResolveAllCheckbox={isViewMode}
                              formName="brsFormUpload"
                              formHelperKey="initiateTransactionChange"
                              updateEffectKey={"updateTransactionUnderReview"}
                              updateEffectPath={`transactionSlice.transactionUnderReview.items.transactions.${officialIndex}`}
                              formId={_get(transaction, "_id")}
                              handleSaveComment={handleSaveComment}
                              comments={brsFormUploadComments}
                            />
                          ) : (
                            <div className="flex items-center justify-between">
                              <label
                                htmlFor="referenceNumber"
                                className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal after:content-['*'] after:ml-0.5 after:text-red-500"
                              >
                                BRS Reference Number
                              </label>
                              <div className="flex flex-col w-full">
                                <Field
                                  name="brsReferenceNumber"
                                  id="referenceNumber"
                                  placeholder="Add BRS Reference Number"
                                  component={Input}
                                  className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                  disabled={transaction?.resolvedFields.includes(
                                    "brsReferenceNumber"
                                  )}
                                />
                              </div>
                            </div>
                          )}

                          <div className="mb-4">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              Upload documents
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Please provide the correct documents as
                              instructed.
                            </p>
                          </div>
                          <Field
                            name="fileUploads"
                            component={FileUploadForm}
                            resolvedFields={_get(transaction, "resolvedFields")}
                            documentData={[
                              ...documents.map((document) => document),
                              ...registrarDecisionDocuments(),
                            ]}
                            onlyPreview={
                              isInBRS || isManualTransaction || isViewMode
                            }
                            showDocumentLabel={
                              isInBRS || isManualTransaction || isViewMode
                            }
                            addReviewComment={isInBRS || isManualTransaction}
                            showResolveAllFieldsCheckbox={
                              isInBRS || isManualTransaction
                            }
                            formName="brsFormUpload"
                            formHelperKey="initiateTransactionChange"
                            updateEffectKey={"updateTransactionUnderReview"}
                            updateEffectPath={`transactionSlice.transactionUnderReview.items.transactions.${officialIndex}`}
                            formId={_get(transaction, "_id")}
                            handleSaveComment={handleSaveComment}
                            comments={brsFormUploadComments}
                          />
                          {isInBRS && (
                            <Field
                              name="fileUploads"
                              component={FileUploadForm}
                              documentData={brsReciptDocs.map((doc) => doc)}
                            />
                          )}
                          {transaction?.status ===
                            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                              .AWAITING_COURT_ORDER && (
                            <div className="flex flex-col space-y-2">
                              <Checkbox
                                name="upholdConversionResolution"
                                label="Uphold the conversion resolution"
                                className="ml-0"
                                checked={values.upholdConversionResolution}
                                onChange={(e) => {
                                  setFieldValue(
                                    "upholdConversionResolution",
                                    e.target.checked
                                  );
                                  if (e.target.checked) {
                                    setFieldValue(
                                      "cancelConversionResolution",
                                      false
                                    ); // Uncheck the other
                                  }
                                }}
                              />
                              <Checkbox
                                name="cancelConversionResolution"
                                label="Cancel the conversion resolution"
                                className="ml-0"
                                checked={values.cancelConversionResolution}
                                onChange={(e) => {
                                  setFieldValue(
                                    "cancelConversionResolution",
                                    e.target.checked
                                  );
                                  if (e.target.checked) {
                                    setFieldValue(
                                      "upholdConversionResolution",
                                      false
                                    ); // Uncheck the other
                                  }
                                }}
                              />
                              {touched.upholdConversionResolution &&
                                touched.cancelConversionResolution &&
                                errors.checkboxes && (
                                  <div className="text-red-500">
                                    {errors.checkboxes}
                                  </div>
                                )}
                            </div>
                          )}

                          {transaction?.status ===
                            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                              .PENDING_REGISTRAR_DECISION && (
                            <div className="flex flex-col space-y-2">
                              <p className="mt-1 text-sm text-gray-500">
                                Please select the decision of the registrar on
                                company restoration
                              </p>
                              <div className="flex space-x-10">
                                <Checkbox
                                  name="registrarApproved"
                                  label="Registrar Approved "
                                  className="ml-0"
                                  checked={values.registrarApproved}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "registrarApproved",
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      setFieldValue("registrarRejected", false); // Uncheck the other

                                      //also revome the registrar rejected file uploaded from the fileuploads field
                                      setFieldValue(
                                        "fileUploads.letterOfRejection",
                                        undefined
                                      );
                                    }
                                  }}
                                />
                                <Checkbox
                                  name="registrarRejected"
                                  label="Registrar Rejected"
                                  className="ml-0"
                                  checked={values.registrarRejected}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "registrarRejected",
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      setFieldValue("registrarApproved", false); // Uncheck the other

                                      //also revome the registrar rejected file uploaded from the fileuploads field
                                      setFieldValue(
                                        "fileUploads.gazetteNotice",
                                        undefined
                                      );
                                    }
                                  }}
                                />
                              </div>
                              {touched.registrarApproved &&
                                touched.registrarRejected &&
                                errors.checkboxes2 && (
                                  <div className="text-red-500">
                                    {errors.checkboxes2}
                                  </div>
                                )}

                              {values.registrarApproved && (
                                <Field
                                  name="fileUploads"
                                  component={FileUploadForm}
                                  documentData={[
                                    {
                                      label: "Gazette Notice",
                                      name: "gazetteNotice",
                                      accept: "application/pdf",
                                    },
                                  ]}
                                />
                              )}
                              {values.registrarRejected && (
                                <Field
                                  name="fileUploads"
                                  component={FileUploadForm}
                                  documentData={[
                                    {
                                      label: "Letter of Rejection",
                                      name: "letterOfRejection",
                                      accept: "application/pdf",
                                    },
                                  ]}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {
                  <div className="flex justify-between space-x-4 mt-6">
                    <button
                      onClick={handleGoBack}
                      className="flex items-start float-right py-2 font-semibold rounded text-primary"
                    >
                      <span className="flex items-center mr-2 h-5">
                        <ChevronLeftIcon
                          className="flex-shrink-0 w-5 h-5"
                          aria-hidden="true"
                        />
                      </span>
                      <p className="font-semibold">Go back</p>
                    </button>

                    {!isViewMode && (
                      <div>
                        {isSubmitting ? (
                          isInUpdateCompanyDetailsContext ? (
                            <button
                              type="submit"
                              className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                              disabled={isSubmitting}
                            >
                              <SpinnerIcon /> Submitting...
                            </button>
                          ) : (
                            <div className="flex items-center justify-center">
                              <SpinnerIcon /> Submitting...
                            </div>
                          )
                        ) : !isRegistrar && !isInBRS && !isLastStep ? (
                          isInUpdateCompanyDetailsContext ? (
                            <button
                              type="submit"
                              className="px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                              disabled={isSubmitting}
                            >
                              Save and Proceed
                            </button>
                          ) : (
                            <div>
                              <button type="submit">Next step</button>
                            </div>
                          )
                        ) : !isRegistrar &&
                          !isInBRS &&
                          isLastStep &&
                          !isInUpdateCompanyDetailsContext &&
                          !isManualTransaction ? (
                          <ESignatureManualDropDown
                            handleEsignature={() => {
                              setFieldValue("eSignatureIsSelected", true);
                              submitForm();
                            }}
                            handleManualSigning={() => {
                              setFieldValue("manualSigningIsSelected", true);
                              submitForm();
                            }}
                            esignatureIsSelected={esignatureIsSelected}
                            isSubmitting={isSubmitting}
                          />
                        ) : isRegistrar || isInUpdateCompanyDetailsContext ? (
                          <button
                            type="submit"
                            className="px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                            disabled={isSubmitting}
                          >
                            {isInUpdateCompanyDetailsContext
                              ? "Save and proceed"
                              : "Submit"}
                          </button>
                        ) : isInBRS ? (
                          <div className={`flex flex-row gap-0.5`}>
                            <button
                              type="submit"
                              className={`flex px-2 py-2 button button-hover button-disabled button-outline button-focus
                                     hover:bg-green-600 hover:shadow-lg text-white bg-sflPrimary rounded-l-md rounded-r-none
                                                  `}
                              onClick={() => {
                                setFieldValue(
                                  "status",
                                  Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                                    .PROCESSED
                                );
                                submitForm();
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <SpinnerIcon />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <div className="ml-1.5 flex items-center">
                                    Process
                                  </div>
                                </>
                              )}
                            </button>
                            <Menu as="div">
                              <Menu.Button
                                ref={trigger}
                                className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                              bg-sflPrimary hover:bg-green-600 hover:shadow-lg  rounded-l-none rounded-r-md
                                                    `}
                              >
                                <span className="sr-only">Open options</span>
                                <ChevronDownIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Menu.Button>
                              <div
                                ref={container}
                                className="z-50 bg-white rounded"
                              >
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <div className="py-2 px-3 rounded">
                                    <Menu.Items>
                                      <div className="py-1">
                                        <Menu.Item>
                                          {({ active }) => (
                                            <button
                                              type="submit"
                                              className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                                                      hover:bg-green-600 hover:shadow-lg rounded bg-sflPrimary w-full
                                                                `}
                                              onClick={() => {
                                                setFieldValue(
                                                  "status",
                                                  Constants
                                                    .MAINTAIN_COMPANY_REVIEW_STATUSES
                                                    .VETTING
                                                );
                                                submitForm();
                                              }}
                                            >
                                              {isSubmitting ? (
                                                <>
                                                  <SpinnerIcon />
                                                  Saving...
                                                </>
                                              ) : (
                                                <>
                                                  <div className="ml-1.5 flex items-center">
                                                    Vet
                                                  </div>
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </Menu.Item>
                                        <div className="mt-1">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                type="submit"
                                                className={`flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus
                                                                      hover:bg-green-600 hover:shadow-lg rounded bg-sflPrimary w-full
                                                                `}
                                                onClick={() => {
                                                  setFieldValue(
                                                    "status",
                                                    Constants
                                                      .MAINTAIN_COMPANY_REVIEW_STATUSES
                                                      .DEFERRED
                                                  );
                                                  submitForm();
                                                }}
                                              >
                                                {isSubmitting ? (
                                                  <>
                                                    <SpinnerIcon />
                                                    Saving...
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="ml-1.5 flex items-center">
                                                      Deffer
                                                    </div>
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </Menu.Item>
                                        </div>
                                      </div>
                                    </Menu.Items>
                                  </div>
                                </Transition>
                              </div>
                            </Menu>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={goToNextStep}
                            className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
                          >
                            <p className="font-semibold">Next step</p>
                            <span className="flex items-center ml-4 h-7">
                              <ChevronRightIcon
                                className="flex-shrink-0 w-5 h-5"
                                aria-hidden="true"
                              />
                            </span>
                          </button>
                        )}
                      </div>
                    )}

                    {isViewMode && (
                      <button
                        type="button"
                        onClick={goToNextStep}
                        className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
                      >
                        <p className="font-semibold">Next step</p>
                        <span className="flex items-center ml-4 h-7">
                          <ChevronRightIcon
                            className="flex-shrink-0 w-5 h-5"
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    )}
                  </div>
                }
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default TransactionUpload;

// ---- save ----
function saveFileToLocalStorage(file, storageKey) {
  const reader = new FileReader();
  reader.onload = () => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        dataUrl: reader.result,
      })
    );
  };
  reader.readAsDataURL(file);
}
function PersistFileUploadsToLocalStorage({ values, storageKey }) {
  useEffect(() => {
    if (values?.fileUploads && Object.values(values.fileUploads).length > 0) {
      const fileObj = {};

      Object.entries(values.fileUploads).forEach(([key, value]) => {
        if (value instanceof File) {
          const reader = new FileReader();
          reader.onload = () => {
            fileObj[key] = {
              path: value?.path,
              name: value?.name,
              type: value?.type,
              dataUrl: reader.result,
            };

            const existing = JSON.parse(localStorage.getItem(storageKey)) || {};

            localStorage.setItem(
              storageKey,
              JSON.stringify({
                ...existing,
                ...fileObj,
              })
            );
          };
          reader.readAsDataURL(value);
        }
      });
    }
  }, [values?.fileUploads, storageKey]);

  return null;
}

// ---- load ----
function getFileFromLocalStorage(storageKey) {
  const metaRaw = localStorage.getItem(storageKey);
  if (!metaRaw) return null;

  const { name, type, lastModified, dataUrl } = JSON.parse(metaRaw);
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  return new File([bytes], name, { type, lastModified });
}
